import { addDoc, collection, updateDoc, deleteDoc, doc, getDocs, query, where, Timestamp, orderBy, CollectionReference, DocumentData, Firestore } from 'firebase/firestore';
import { db } from '../config/firebase';
import { isLocalMode, getLocalList, setLocalList, generateLocalId } from '../utils/localModeHelper';

export interface Expense {
  id?: string;
  type: 'basic' | 'daily';
  category: string;
  amount: number;
  description: string;
  date: Date;
  createdAt: Date;
  updatedAt: Date;
}

const EXPENSES_LS_KEY = 'expenses_local';

const ensureFirestore = (): Firestore => {
  if (!db) throw new Error('Firestore is not initialized');
  return db;
};

const getExpensesCollection = (): CollectionReference<DocumentData> => collection(ensureFirestore(), 'expenses');

const toDate = (val: any): Date => {
  if (!val) return new Date();
  if (val instanceof Date) return val;
  if (val instanceof Timestamp) return val.toDate();
  if (typeof val === 'string' || typeof val === 'number') return new Date(val);
  return new Date();
};

export const addExpense = async (expense: Omit<Expense, 'id' | 'createdAt' | 'updatedAt'>): Promise<Expense> => {
  try {
    const now = new Date();
    const base: Expense = { ...expense, createdAt: now, updatedAt: now };

    if (isLocalMode()) {
      const newExp: Expense = { ...base, id: generateLocalId('exp') };
      const list = getLocalList<Expense>(EXPENSES_LS_KEY);
      list.push(newExp);
      setLocalList(EXPENSES_LS_KEY, list);
      return newExp;
    }

    const docRef = await addDoc(getExpensesCollection(), {
      ...expense,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    return { ...base, id: docRef.id };
  } catch (error) {
    console.error('Error adding expense:', error);
    throw new Error('فشل في إضافة المصروف');
  }
};

export const updateExpense = async (id: string, expense: Partial<Expense>): Promise<void> => {
  try {
    if (isLocalMode()) {
      const list = getLocalList<Expense>(EXPENSES_LS_KEY);
      const idx = list.findIndex(e => e.id === id);
      if (idx >= 0) {
        list[idx] = { ...list[idx], ...expense, updatedAt: new Date() };
        setLocalList(EXPENSES_LS_KEY, list);
      }
      return;
    }
    await updateDoc(doc(getExpensesCollection(), id), { ...expense, updatedAt: Timestamp.now() });
  } catch (error) {
    console.error('Error updating expense:', error);
    throw new Error('فشل في تحديث المصروف');
  }
};

export const deleteExpense = async (id: string): Promise<void> => {
  try {
    if (isLocalMode()) {
      const list = getLocalList<Expense>(EXPENSES_LS_KEY).filter(e => e.id !== id);
      setLocalList(EXPENSES_LS_KEY, list);
      return;
    }
    await deleteDoc(doc(getExpensesCollection(), id));
  } catch (error) {
    console.error('Error deleting expense:', error);
    throw new Error('فشل في حذف المصروف');
  }
};

export const getAllExpenses = async (): Promise<Expense[]> => {
  try {
    if (isLocalMode()) {
      return getLocalList<Expense>(EXPENSES_LS_KEY);
    }
    const snapshot = await getDocs(getExpensesCollection());
    return snapshot.docs.map(d => {
      const data = d.data();
      return { id: d.id, ...data, date: toDate(data.date), createdAt: toDate(data.createdAt), updatedAt: toDate(data.updatedAt) } as Expense;
    });
  } catch (error) {
    console.error('Error getting expenses:', error);
    return [];
  }
};

export const getExpensesByType = async (type: 'basic' | 'daily'): Promise<Expense[]> => {
  try {
    if (isLocalMode()) {
      return getLocalList<Expense>(EXPENSES_LS_KEY)
        .filter(e => e.type === type)
        .sort((a, b) => toDate(b.date).getTime() - toDate(a.date).getTime());
    }
    const q = query(getExpensesCollection(), where('type', '==', type), orderBy('date', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => {
      const data = d.data();
      return { id: d.id, ...data, date: toDate(data.date), createdAt: toDate(data.createdAt), updatedAt: toDate(data.updatedAt) } as Expense;
    });
  } catch (error) {
    console.error('Error getting expenses by type:', error);
    return [];
  }
};

export const getExpensesByDate = async (date: Date): Promise<Expense[]> => {
  try {
    const startOfDay = new Date(date); startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(date); endOfDay.setHours(23, 59, 59, 999);

    if (isLocalMode()) {
      return getLocalList<Expense>(EXPENSES_LS_KEY)
        .filter(e => toDate(e.date) >= startOfDay && toDate(e.date) <= endOfDay)
        .sort((a, b) => toDate(b.date).getTime() - toDate(a.date).getTime());
    }
    const q = query(getExpensesCollection(), where('date', '>=', startOfDay), where('date', '<=', endOfDay), orderBy('date', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => {
      const data = d.data();
      return { id: d.id, ...data, date: toDate(data.date), createdAt: toDate(data.createdAt), updatedAt: toDate(data.updatedAt) } as Expense;
    });
  } catch (error) {
    console.error('Error getting expenses by date:', error);
    return [];
  }
};

export const calculateTotalExpenses = async (type?: 'basic' | 'daily'): Promise<number> => {
  try {
    const expenses = type ? await getExpensesByType(type) : await getAllExpenses();
    return expenses.reduce((sum, e) => sum + e.amount, 0);
  } catch (error) {
    console.error('Error calculating total expenses:', error);
    return 0;
  }
};

export const calculateDailyProfit = async (date: Date): Promise<number> => {
  try {
    const dailyExpenses = await getExpensesByDate(date);
    const totalDaily = dailyExpenses.filter(e => e.type === 'daily').reduce((sum, e) => sum + e.amount, 0);
    return 0 - totalDaily;
  } catch (error) {
    console.error('Error calculating daily profit:', error);
    return 0;
  }
};

export const calculateMonthlyBasicExpenses = async (month: number, year: number): Promise<number> => {
  try {
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0);
    const expenses = await getAllExpenses();
    return expenses
      .filter(e => e.type === 'basic' && toDate(e.date) >= startDate && toDate(e.date) <= endDate)
      .reduce((sum, e) => sum + e.amount, 0);
  } catch (error) {
    console.error('Error calculating monthly basic expenses:', error);
    return 0;
  }
};

export const calculateMonthlyDailyExpenses = async (month: number, year: number): Promise<number> => {
  try {
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0);
    const expenses = await getAllExpenses();
    return expenses
      .filter(e => e.type === 'daily' && toDate(e.date) >= startDate && toDate(e.date) <= endDate)
      .reduce((sum, e) => sum + e.amount, 0);
  } catch (error) {
    console.error('Error calculating monthly daily expenses:', error);
    return 0;
  }
};

export const getExpensesByDateRange = async (startDate: Date, endDate: Date): Promise<Expense[]> => {
  try {
    if (isLocalMode()) {
      return getLocalList<Expense>(EXPENSES_LS_KEY)
        .filter(e => e.type === 'daily' && toDate(e.date) >= startDate && toDate(e.date) <= endDate)
        .sort((a, b) => toDate(b.date).getTime() - toDate(a.date).getTime());
    }
    const q = query(getExpensesCollection(), where('type', '==', 'daily'));
    const snapshot = await getDocs(q);
    const allExpenses = snapshot.docs.map(d => {
      const data = d.data();
      return { id: d.id, ...data, date: toDate(data.date), createdAt: toDate(data.createdAt), updatedAt: toDate(data.updatedAt) } as Expense;
    });
    return allExpenses
      .filter(e => e.date >= startDate && e.date <= endDate)
      .sort((a, b) => b.date.getTime() - a.date.getTime());
  } catch (error) {
    console.error('Error getting expenses by date range:', error);
    return [];
  }
};

export const calculateDailyExpensesForDateRange = async (startDate: Date, endDate: Date): Promise<Record<string, number>> => {
  try {
    const expenses = await getExpensesByDateRange(startDate, endDate);
    const dailyExpenses: Record<string, number> = {};
    expenses.forEach(expense => {
      const dateStr = expense.date.toISOString().split('T')[0];
      dailyExpenses[dateStr] = (dailyExpenses[dateStr] || 0) + expense.amount;
    });
    return dailyExpenses;
  } catch (error) {
    console.error('Error calculating daily expenses for date range:', error);
    return {};
  }
};
